import { useCallback, useEffect, useRef, useState } from "react";
import lodash from "lodash";
import { searchModule, searchModuleV2 } from "../api/homePageApi";
import normalizeRecord from "../api/normalizeRecord";

/**
 * Shared, module-agnostic search hook (spec §3.3a). Every module's search
 * reuses this exact hook — only `moduleKey` and `normalize` are module-specific.
 * Debounce delay matches DiscoveryV2/History/HistoryV2.js's own search (400ms)
 * for consistent feel across the app.
 *
 * const search = useModuleSearch({ moduleKey: 'discovery', customerId, userId, normalize: normalizeSearchRecord });
 */
export default function useModuleSearch({
  moduleKey,
  customerId,
  userId,
  normalize = normalizeRecord,
  debounceMs = 400,
  limit = 5,
  apiVersion = 1, // 2 = merged Brand Assessment + Category search (getHomePageDataV2/search)
  includeCategory = true,
}) {
  const [query, setQueryState] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const requestIdRef = useRef(0);

  const runSearch = useCallback(
    async (searchTerm, targetPage) => {
      if (!searchTerm) {
        setResults([]);
        setTotalPages(1);
        setTotalCount(0);
        setLoading(false);
        return;
      }
      const requestId = ++requestIdRef.current;
      setLoading(true);
      try {
        const data =
          apiVersion === 2
            ? await searchModuleV2({ customerId, userId, search: searchTerm, page: targetPage, limit, includeCategory })
            : await searchModule({
                moduleKey,
                customerId,
                userId,
                search: searchTerm,
                page: targetPage,
                limit,
              });
        if (requestId !== requestIdRef.current) return; // stale response, a newer search superseded it
        // Tolerates either envelope: the search endpoint's own { data: [...] }
        // and the list endpoint's { records: [...] } flat shape.
        setResults((data?.data || data?.records || []).map(normalize).filter(Boolean));
        setTotalPages(data?.totalPages || 1);
        setTotalCount(data?.totalCount || 0);
      } catch {
        if (requestId !== requestIdRef.current) return;
        setResults([]);
        setTotalPages(1);
        setTotalCount(0);
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    },
    [moduleKey, customerId, userId, normalize, limit, apiVersion, includeCategory]
  );

  const debouncedSearch = useRef(lodash.debounce((term) => runSearch(term, 1), debounceMs)).current;

  useEffect(() => {
    setPage(1);
    if (!query) {
      setResults([]);
      setTotalPages(1);
      setTotalCount(0);
      debouncedSearch.cancel();
      return;
    }
    debouncedSearch(query);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  const setQuery = useCallback((value) => setQueryState(value), []);

  const setPageAndFetch = useCallback(
    (nextPage) => {
      setPage(nextPage);
      runSearch(query, nextPage);
    },
    [query, runSearch]
  );

  return {
    query,
    setQuery,
    results,
    loading,
    page,
    totalPages,
    totalCount,
    setPage: setPageAndFetch,
    isActive: !!query,
  };
}
